import type { ResolveDistanceGapInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { ApiError } from '../../../shared/shared.api';
import { getDistanceTrip, resolveDistanceGap, type DistanceTripDetail } from '../distance-agreement.api';
import { resolvedToast } from '../policies/distance-labels.policy';

export type TripDistanceState =
  | { status: 'loading' }
  | { status: 'loaded'; trip: DistanceTripDetail }
  | { status: 'forbidden' }
  | { status: 'not_found' }
  | { status: 'error'; error: unknown };

function toErrorState(error: unknown): TripDistanceState {
  if (error instanceof ApiError && error.status === 403) return { status: 'forbidden' };
  if (error instanceof ApiError && error.status === 404) return { status: 'not_found' };
  return { status: 'error', error };
}

/** One trip's comparison with its sources, and the resolve action for someone who may. A refused resolution keeps its message for the drawer. */
export function useTripDistance(tripId: string) {
  const toast = useToast();
  const { state: user } = useCurrentUser();
  const [state, setState] = useState<TripDistanceState>({ status: 'loading' });
  const [isResolving, setIsResolving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDistanceTrip(tripId)
      .then((trip) => {
        if (!cancelled) setState({ status: 'loaded', trip });
      })
      .catch((error) => {
        if (!cancelled) setState(toErrorState(error));
      });
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  /** True once the server has recorded it; the toast follows that, not the click. */
  async function resolve(input: ResolveDistanceGapInput): Promise<boolean> {
    setIsResolving(true);
    setRefusal(null);
    try {
      const trip = await resolveDistanceGap(tripId, input);
      setState({ status: 'loaded', trip });
      toast.success(resolvedToast(trip.number));
      return true;
    } catch (error) {
      setRefusal(apiErrorMessage(error) ?? 'Could not record this answer. Try again.');
      return false;
    } finally {
      setIsResolving(false);
    }
  }

  const permissions = user.status === 'authenticated' ? user.user.permissions : [];
  return { state, canResolve: permissions.includes('distance.resolve'), isResolving, refusal, clearRefusal: () => setRefusal(null), resolve };
}
